import { AbstractControl, AsyncValidator, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Equipment } from '../../../../models/equipment.model';
import { ContainerGrade } from '../../../../models/container-grade.model';
import { Mode } from '../../../../models/mode.model';
import { inject, Injectable } from '@angular/core';
import { combineLatest, forkJoin, map, Observable, take } from 'rxjs';
import { MasterDataService } from '../../../../services/master-data.service';
import { BulkWorkOrderSharedDataService } from '../../../../services/bulk-work-order-shared-data.service';
import { AemsValidationError } from '../../../../models/aems-validation-error.model';

/**
 * Maersk containers are assigned grades based on the condition of the containers
 * The grades are
 * E-General cargo shipments
 * K-General cargo - Clean container/Premium shipments
 * M-Food grade type shipments
 * S-Flexi-bags shipments and Premium Food grade
 * As evident Q is the worst grade possible and is used to carry scrap cargo shipments
 * The container grade validation makes sure that there is no food grade upgrade request for grade type Q as they
 * cannot and should not be upgraded to food grade and should be used for scrap and general cargo shipments only
 * which are grades E,K and Q
 * Bottomline grade Q containers can only be upgraded to grade E or K
 * @param equipment
 * @param containerGrades
 * @param mode
 * @returns
 */
@Injectable({ providedIn: 'root' })
export class ContainerGradeValidator {
  private masterdataService: MasterDataService;
  private bulkWorkOrderSharedDataService: BulkWorkOrderSharedDataService;
  constructor() {
    this.masterdataService = inject(MasterDataService);
    this.bulkWorkOrderSharedDataService = inject(BulkWorkOrderSharedDataService);
  }
  validate(repairCodes: string[]): Observable<Map<string, string[]>> {
    const values = repairCodes;
    return combineLatest([
      this.masterdataService.getContainerGrades().pipe(take(1)),
      this.bulkWorkOrderSharedDataService.ValidatedEquipments,
      this.bulkWorkOrderSharedDataService.BulkWorkOrderHeader,
      this.bulkWorkOrderSharedDataService.Mode,
    ]).pipe(
      map(([containerGrades, validEquipments, bulkWorkOrderHeader, mode]) => {
        const errors = new Map<string, string[]>();
        const enteredEquipmentNumbers = bulkWorkOrderHeader.equipments.map((x) => x.equipmentNumber);
        const equipments = validEquipments.filter((x) => enteredEquipmentNumbers.includes(x.equipmentNumber ?? ''));
        const disallowedGrades = containerGrades.filter((x) => x.notApplicableRepairCodes.length > 0);
        disallowedGrades.forEach((disallowedGrade) => {
          const filteredEquipments = equipments.filter((x) => x.gradeCode === disallowedGrade.containerGradeCode);
          values.forEach((value) => {
            const erroringEquipmentNumbers: string[] = [];
            filteredEquipments.forEach((filteredEquipment) => {
              if (filteredEquipment.gradeCode === disallowedGrade.containerGradeCode) {
                if (disallowedGrade.repairMode === mode?.code) {
                  if (disallowedGrade.notApplicableRepairCodes.includes(value)) {
                    erroringEquipmentNumbers.push(filteredEquipment.equipmentNumber ?? '');
                  }
                }
              }
            });
            errors.set(value, erroringEquipmentNumbers);
          });
        });
        return errors;
      }),
    );
  }
}
